import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { RichTextEditor } from "@/components/RichTextEditor";
import { Send, Eye, FlaskConical } from "lucide-react";

export const NewsletterComposer = ({ subscriberCount, onSent }) => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [form, setForm] = useState({ subject: "", html_content: "" });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");
  const ready = form.subject.trim() && form.html_content.trim();

  const openPreview = async () => {
    setBusy("preview");
    try {
      const res = await axios.post(`${API}/admin/newsletter/render`, form, { headers });
      setPreview(res.data.html);
    } catch (error) {
      toast.error("Could not render preview");
    } finally {
      setBusy("");
    }
  };

  const sendTest = async () => {
    setBusy("test");
    try {
      const res = await axios.post(`${API}/admin/newsletter/test`, form, { headers });
      toast.success(`Test sent to ${res.data.sent_to}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send test");
    } finally {
      setBusy("");
    }
  };

  const sendAll = async () => {
    if (!window.confirm(`Send "${form.subject}" to ${subscriberCount} subscriber(s) now? This cannot be undone.`)) return;
    setBusy("send");
    try {
      const res = await axios.post(`${API}/admin/send-mass-email`, form, { headers });
      toast.success(res.data.message);
      setForm({ subject: "", html_content: "" });
      setPreview(null);
      onSent?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send newsletter");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="glass rounded-xl p-6" data-testid="newsletter-composer">
      <h2 className="font-heading text-xl text-charcoal mb-1">Compose Newsletter</h2>
      <p className="font-body text-xs text-taupe mb-4">Sent from Kalani Luana &lt;inquiries@meetkalani.com&gt;, replies go to meetkalaniluana@gmail.com. Your content is wrapped in the branded Inner Circle template with an unsubscribe link.</p>
      <div className="space-y-4">
        <div>
          <Label className="font-body text-sm text-stone-grey mb-2 block">Subject</Label>
          <Input data-testid="newsletter-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="A note from Kalani..." className="form-input" />
        </div>
        <div>
          <Label className="font-body text-sm text-stone-grey mb-2 block">Message</Label>
          <RichTextEditor value={form.html_content} onChange={(html) => setForm((prev) => ({ ...prev, html_content: html }))} placeholder="Write your newsletter…" />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" disabled={!ready || !!busy} onClick={openPreview} data-testid="newsletter-preview-btn">
            <Eye className="w-4 h-4 mr-2" /> Preview
          </Button>
          <Button type="button" variant="outline" disabled={!ready || !!busy} onClick={sendTest} data-testid="newsletter-test-btn">
            <FlaskConical className="w-4 h-4 mr-2" /> {busy === "test" ? "Sending…" : "Send test to myself"}
          </Button>
          <Button type="button" className="btn-primary ml-auto" disabled={!ready || !!busy || subscriberCount === 0} onClick={sendAll} data-testid="newsletter-send-btn">
            <Send className="w-4 h-4 mr-2" /> {busy === "send" ? "Sending…" : `Send to ${subscriberCount} subscriber${subscriberCount === 1 ? "" : "s"}`}
          </Button>
        </div>
      </div>

      <Dialog open={preview !== null} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-3xl" data-testid="newsletter-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-charcoal">Email preview</DialogTitle>
            <DialogDescription className="font-body text-stone-grey">This is how subscribers will see it.</DialogDescription>
          </DialogHeader>
          <iframe title="Newsletter preview" srcDoc={preview || ""} className="w-full h-[70vh] rounded-lg border border-sand bg-white" data-testid="newsletter-preview-frame" />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setPreview(null)}>Close</Button>
            <Button type="button" className="btn-primary" disabled={subscriberCount === 0 || !!busy} onClick={sendAll} data-testid="newsletter-preview-send-btn"><Send className="w-4 h-4 mr-2" /> Send to {subscriberCount}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
